// Quản lý API key cho các hệ thống NGOÀI gọi vào backend (chỉ admin thấy nút mở):
//   - Bot đăng bài Kênh cộng đồng
//   - Site tiện ích (yt.muasam.cc…) gửi thông báo kết quả xử lý về app
// Một kho key chung, phân biệt bằng "phạm vi" — thêm hệ thống mới KHÔNG phải thêm
// biến .env rồi deploy lại, và thu hồi được ngay.
// Key gốc chỉ hiện ĐÚNG một lần lúc tạo — server chỉ giữ bản băm.
//
// File này giữ phần gọi API + trạng thái; form cấp key và bảng nằm ở 2 file riêng.
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Input, Alert, message } from "antd";
import communityApi from "../../../api/communityApi";
import ApiKeyCreateForm from "./api-key-create-form";
import ApiKeyTable from "./api-key-table";

const SCOPE_COMMUNITY = "community";
const SCOPE_UTILITY = "utility-notify";
const SCOPE_LABELS = {
    [SCOPE_COMMUNITY]: "Bot đăng bài",
    [SCOPE_UTILITY]: "Tiện ích gửi thông báo",
};
const SCOPE_OPTIONS = [
    { value: SCOPE_COMMUNITY, label: SCOPE_LABELS[SCOPE_COMMUNITY] },
    { value: SCOPE_UTILITY, label: SCOPE_LABELS[SCOPE_UTILITY] },
];

const EMPTY_FORM = { name: "", userId: null, scope: SCOPE_COMMUNITY, site: "" };

export default function CommunityApiKeysModal({ open, onClose }) {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [creating, setCreating] = useState(false);
    const [newKey, setNewKey] = useState(null); // key gốc vừa tạo, hiện 1 lần

    const fetchKeys = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await communityApi.getApiKeys();
            setKeys(data.keys || []);
        } catch {
            message.error("Không thể tải danh sách API key");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        setNewKey(null);
        fetchKeys();
    }, [open, fetchKeys]);

    const patchForm = (changes) => setForm((prev) => ({ ...prev, ...changes }));

    const handleCreate = async () => {
        const { name, userId, scope, site } = form;
        if (!name.trim()) return message.error("Nhập tên key (vd: bot telegram)");
        if (!userId) return message.error("Nhập userId tài khoản gắn với key");
        if (scope === SCOPE_UTILITY && !site.trim()) {
            return message.error("Nhập slug tiện ích (vd: yt)");
        }
        try {
            setCreating(true);
            const { data } = await communityApi.createApiKey(
                name.trim(),
                userId,
                scope,
                scope === SCOPE_UTILITY ? site.trim() : undefined
            );
            setNewKey(data.key);
            setForm({ ...EMPTY_FORM });
            fetchKeys();
        } catch (err) {
            message.error(err?.response?.data?.message || "Không tạo được key");
        } finally {
            setCreating(false);
        }
    };

    const handleRevoke = async (id) => {
        try {
            await communityApi.revokeApiKey(id);
            message.success("Đã thu hồi key");
            fetchKeys();
        } catch (err) {
            message.error(err?.response?.data?.message || "Không thu hồi được key");
        }
    };

    const handleDelete = async (id) => {
        try {
            await communityApi.deleteApiKey(id);
            message.success("Đã xoá key");
            fetchKeys();
        } catch (err) {
            message.error(err?.response?.data?.message || "Không xoá được key");
        }
    };

    /** Copy key vừa tạo — bôi đen chuỗi 50 ký tự bằng tay rất dễ sót đầu/cuối. */
    const copyNewKey = async () => {
        try {
            await navigator.clipboard.writeText(newKey);
            message.success("Đã copy key");
        } catch {
            message.error("Trình duyệt chặn copy — bôi đen rồi Ctrl+C");
        }
    };

    return (
        <Modal
            title="API key cho hệ thống ngoài"
            open={open}
            onCancel={onClose}
            footer={null}
            width={980}
        >
            <Alert
                type="info"
                showIcon
                className="mb-4"
                message="Key thay cho mật khẩu"
                description='Đưa key cho bên ngoài thay vì mật khẩu tài khoản. Mỗi key chỉ dùng được ĐÚNG phạm vi của nó: "Bot đăng bài" chỉ đăng/sửa bài cộng đồng, "Tiện ích gửi thông báo" chỉ bắn thông báo cho đúng tiện ích đã gắn — không đụng ví, ngân hàng hay danh mục. Bên gọi gửi kèm header X-Api-Key. Thu hồi bất cứ lúc nào mà không phải đổi mật khẩu.'
            />

            {newKey && (
                <Alert
                    type="success"
                    showIcon
                    className="mb-4"
                    message="Key vừa tạo — copy ngay, đóng bảng này là không xem lại được"
                    description={
                        <div className="flex gap-2 mt-1">
                            <Input readOnly value={newKey} />
                            <Button onClick={copyNewKey}>Copy</Button>
                        </div>
                    }
                />
            )}

            <div className="mb-5 pl-3 border-l-4 border-blue-400">
                <div className="font-semibold text-blue-700">Cấp key mới</div>
                <div className="text-xs text-gray-500 mb-2">
                    Key gốc chỉ hiện một lần ngay sau khi tạo, server chỉ giữ bản băm.
                </div>
                <ApiKeyCreateForm
                    scopeOptions={SCOPE_OPTIONS}
                    utilityScope={SCOPE_UTILITY}
                    name={form.name}
                    userId={form.userId}
                    scope={form.scope}
                    site={form.site}
                    creating={creating}
                    onChange={patchForm}
                    onCreate={handleCreate}
                />
            </div>

            <div className="pl-3 border-l-4 border-green-500">
                <div className="font-semibold text-green-700">Key đã cấp</div>
                <div className="text-xs text-gray-500 mb-2">
                    Thu hồi = chặn key nhưng giữ lịch sử. Xoá = dọn hẳn dòng, không khôi phục được.
                </div>
                <ApiKeyTable
                    keys={keys}
                    loading={loading}
                    scopeLabels={SCOPE_LABELS}
                    utilityScope={SCOPE_UTILITY}
                    onRevoke={handleRevoke}
                    onDelete={handleDelete}
                />
            </div>
        </Modal>
    );
}
